/**
 * The one who lives on the front door.
 *
 * It blinks, it watches where your finger is, and it is pleased to see how
 * many pictures you have kept. That is the whole of it: no streak, no badge,
 * no reward for coming back — just somebody who is glad you did. A child does
 * not need to be paid to open a drawing app; they need it to feel inhabited.
 */

import { useEffect, useRef } from 'preact/hooks';
import type { JSX } from 'preact';
import type { Mood } from '../engine/character.ts';
import { characterFrom, drawCharacter } from '../engine/character.ts';
import { token } from '../engine/palette.ts';

const MAX_DPR = 2;
/** Fixed seed: it is the same somebody every time the app opens. */
const SEED = 20260726;

interface Props {
  /** How many pictures are on the shelf — it is happier when there are some. */
  kept: number;
}

export function Companion({ kept }: Props): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  const lookRef = useRef({ x: 0, y: 0 });
  const keptRef = useRef(kept);
  keptRef.current = kept;

  // It looks towards wherever the child last touched or pointed.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const canvas = ref.current;
      if (!canvas) return;
      const r = canvas.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      // Clamped: eyes that track all the way to the edge look unsettling.
      lookRef.current = {
        x: Math.max(-1, Math.min(1, (e.clientX - cx) / 260)),
        y: Math.max(-1, Math.min(1, (e.clientY - cy) / 260)),
      };
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerdown', onMove);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onMove);
    };
  }, []);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const character = characterFrom(SEED, token('--crayon-yellow'));
    let raf = 0;
    let start = performance.now();

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0) return;
      const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      const bw = Math.round(rect.width * dpr);
      const bh = Math.round(rect.height * dpr);
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const t = Math.max(0, (now - start) / 1000);
      if (t > 3600) start = now;

      const size = Math.min(rect.width, rect.height) * 0.38;
      const look = lookRef.current;
      const mood: Mood = keptRef.current > 0 ? 'happy' : 'calm';

      drawCharacter(ctx, character, {
        x: rect.width / 2 + look.x * size * 0.12,
        y: rect.height / 2 + Math.sin(t * 1.6) * size * 0.07 + look.y * size * 0.08,
        size,
        t,
        mood,
        angle: look.x * 0.1,
      });
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas class="companion" ref={ref} aria-hidden="true" />;
}
